import { useEffect, useState } from 'react';
import { X, Search, Pencil, Check, Loader2 } from 'lucide-react';
import PermissionTree from './PermissionTree';
import type { PermissionInfo } from '../types';

interface RolePermissionsModalProps {
  isOpen: boolean;
  onClose: () => void;
  roleKey: string;
  roleLabel: string;
  roleDescription?: string;
  orderedCategories: string[];
  categoriesMap: Record<string, PermissionInfo[]>;
  grantedPermissions: string[];
  isEditing: boolean;
  editedPermissions: string[];
  canEdit: boolean;
  isSaving: boolean;
  onEditStart: () => void;
  onEditCancel: () => void;
  onSave: () => void;
  onTogglePermission: (key: string) => void;
  onToggleCategoryPermissions: (category: string, checked: boolean) => void;
}

export default function RolePermissionsModal({
  isOpen,
  onClose,
  roleKey,
  roleLabel,
  roleDescription,
  orderedCategories,
  categoriesMap,
  grantedPermissions,
  isEditing,
  editedPermissions,
  canEdit,
  isSaving,
  onEditStart,
  onEditCancel,
  onSave,
  onTogglePermission,
  onToggleCategoryPermissions,
}: RolePermissionsModalProps) {
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(new Set());

  // Reset transient UI state (search, expanded nodes) whenever the modal is
  // (re)opened for a role, defaulting to expanding categories that already
  // have granted permissions so the tree opens showing what's checked.
  useEffect(() => {
    if (!isOpen) return;
    const granted = new Set(grantedPermissions);
    setSearchQuery('');
    setExpandedCategories(
      new Set(
        orderedCategories.filter((category) =>
          (categoriesMap[category] || []).some((p) => granted.has(p.key))
        )
      )
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, roleKey]);

  if (!isOpen) return null;

  const selected = new Set(isEditing ? editedPermissions : grantedPermissions);
  const totalAvailable = orderedCategories.reduce((sum, c) => sum + (categoriesMap[c]?.length || 0), 0);

  const handleToggleExpand = (category: string) => {
    setExpandedCategories((prev) => {
      const next = new Set(prev);
      if (next.has(category)) next.delete(category);
      else next.add(category);
      return next;
    });
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex justify-between items-start p-6 border-b">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">{roleLabel}</h2>
            {roleDescription && <p className="text-sm text-gray-500 mt-1">{roleDescription}</p>}
            <p className="text-xs text-gray-400 mt-1">
              {selected.size} di {totalAvailable} permessi assegnati
            </p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 flex-shrink-0">
            <X className="h-6 w-6" />
          </button>
        </div>

        {/* Toolbar */}
        <div className="flex items-center gap-3 px-6 py-3 border-b bg-gray-50">
          <div className="relative flex-1">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Cerca permesso..."
              className="w-full pl-8 pr-3 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <button
            onClick={() => setExpandedCategories(new Set(orderedCategories))}
            className="text-xs text-gray-600 hover:text-gray-900 whitespace-nowrap"
          >
            Espandi tutto
          </button>
          <button
            onClick={() => setExpandedCategories(new Set())}
            className="text-xs text-gray-600 hover:text-gray-900 whitespace-nowrap"
          >
            Comprimi tutto
          </button>
        </div>

        {/* Tree */}
        <div className="flex-1 overflow-auto p-6">
          <PermissionTree
            orderedCategories={orderedCategories}
            categoriesMap={categoriesMap}
            selected={selected}
            editable={isEditing}
            onToggle={onTogglePermission}
            onToggleCategory={onToggleCategoryPermissions}
            expandedCategories={expandedCategories}
            onToggleExpand={handleToggleExpand}
            searchQuery={searchQuery}
          />
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-3 px-6 py-4 border-t bg-gray-50">
          {isEditing ? (
            <>
              <button
                onClick={onEditCancel}
                disabled={isSaving}
                className="px-4 py-2 text-sm font-medium border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
              >
                Annulla
              </button>
              <button
                onClick={onSave}
                disabled={isSaving}
                className="flex items-center gap-1.5 px-4 py-2 text-sm font-medium bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
              >
                {isSaving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
                Salva
              </button>
            </>
          ) : (
            <>
              <button
                onClick={onClose}
                className="px-4 py-2 text-sm font-medium border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Chiudi
              </button>
              {canEdit && (
                <button
                  onClick={onEditStart}
                  className="flex items-center gap-1.5 px-4 py-2 text-sm font-medium bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  <Pencil className="h-4 w-4" />
                  Modifica permessi
                </button>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
